import { getMarkdownComponents } from '@backrs_ui/common-ui/src/molecules/Markdown/markdownComponents';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { PlanStats } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const STRINGS = appStrings.modules.satActBooster.results;

type PlanAtAGlanceProps = Readonly<{
  summary: string | null;
  stats: PlanStats;
}>;

/**
 * "Your Plan at a Glance": the plan summary on the left, three stat tiles stacked
 * on the right (points to gain, total weeks, hours/week).
 */
export const PlanAtAGlance = ({ summary, stats }: PlanAtAGlanceProps) => {
  const tiles = [
    ...(stats.pointsToGain !== null
      ? [
          {
            id: 'points-to-gain',
            label: STRINGS.stats.pointsToGain,
            value: `+${stats.pointsToGain}`,
          },
        ]
      : []),
    { id: 'total-weeks', label: STRINGS.stats.totalWeeks, value: stats.totalWeeks },
    { id: 'hours-per-week', label: STRINGS.stats.hoursPerWeek, value: stats.hoursPerWeek },
  ];

  return (
    <Box sx={rootSx} id="booster-plan-at-a-glance">
      <Box sx={summaryColSx}>
        <Typography variant="display-md-semibold" sx={titleSx}>
          {STRINGS.planAtAGlance.title}
        </Typography>
        <Box sx={summaryBodySx}>
          {summary ? (
            <ReactMarkdown components={getMarkdownComponents('XLARGE')}>{summary}</ReactMarkdown>
          ) : (
            <Typography variant="text-lg-regular" sx={mutedSx}>
              {STRINGS.planAtAGlance.empty}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={tilesColSx}>
        {tiles.map((tile) => (
          <Box key={tile.id} sx={tileSx} id={`booster-stat-${tile.id}`}>
            <Typography variant="text-sm-medium" sx={tileLabelSx}>
              {tile.label}
            </Typography>
            <Typography variant="display-sm-semibold" sx={tileValueSx}>
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-3xl'],
});

const summaryColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: { xs: 'none', md: '1 1 0' },
  minWidth: 0,
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const summaryBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  '& p:first-of-type': { marginTop: 0 },
  '& p:last-of-type': { marginBottom: 0 },
  // The markdown renderer sets font-size inline, so override needs !important to win.
  '& p, & li': { fontSize: '18px !important', lineHeight: '1.5rem !important' },
  maxWidth: {
    xs: '100%',
    lg: '800px',
  },
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const tilesColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row', md: 'column' },
  gap: backrs.spacing['spacing-2xl'],
  width: { xs: '100%', md: '258px' },
  flexShrink: 0,
});

const tileSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: { xs: '1 1 0', md: 'none' },
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const tileLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const tileValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
